/**
 * Task View Page - Opens a task modal directly from a shareable link
 */
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import { taskAPI } from '@/api/taskAPI';
import { commentAPI } from '@/api/commentAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { mastersAPI, WorkflowStatus, Priority } from '@/api/mastersApi';
import { projectAPI } from '@/api/projectAPI';
import { Loader } from '@/components/Loader';
import { Task } from '@/types';
import { Comment } from '@/types/comments';
import { deleteComment, updateComment } from '@/utils/commentUtils';
import { updateTask } from '@/store/taskSlice';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { subtaskAPI } from '@/api/subtaskAPI';
import { buildTaskShareTitle } from '@/utils/documentTitle';

export const TaskView = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((state) => state.projects);

  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<Task | null>(null);
  const [projectKey, setProjectKey] = useState<string>('AHM');
  const hasLoadedRef = useRef(false);
  const isOpeningSubtaskRef = useRef(false);

  // Update projectKey when currentProject changes (only if we have a task)
  useEffect(() => {
    if (currentProject?.key && task) {
      setProjectKey(currentProject.key);
    }
  }, [currentProject?.key, task]);

  useEffect(() => {
    if (!task) {
      return;
    }

    const derivedProjectKey =
      projectKey ||
      (task as any)?.project_key ||
      (task as any)?.projectKey ||
      (task as any)?.project?.key ||
      (task as any)?.project_code ||
      currentProject?.key ||
      null;

    const fallbackIdentifier =
      (task as any)?.task_key ||
      (task as any)?.code ||
      (task as any)?.display_code ||
      (task as any)?.ticket_key ||
      null;

    const displayNumber =
      (task as any)?.display_number ??
      (task as any)?.displayNumber ??
      (task as any)?.number ??
      null;

    document.title = buildTaskShareTitle({
      projectKey: derivedProjectKey,
      displayNumber,
      fallbackId: fallbackIdentifier,
      taskId: task.id,
      title: task.title,
    });
  }, [task, projectKey, currentProject?.key]);

  // Reset hasLoadedRef when taskId changes (e.g., when navigating from subtask to parent task)
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [taskId]);

  // Load task data - only once
  useEffect(() => {
    if (!taskId || hasLoadedRef.current) {
      if (!taskId) {
        setError('Task ID is required');
        setLoading(false);
      }
      return;
    }

    const loadTask = async () => {
      try {
        hasLoadedRef.current = true;
        setLoading(true);
        const taskData = await taskAPI.getTask(taskId);
        setTask(taskData);

        // Get project key from API (non-blocking, doesn't delay modal opening)
        if (taskData.project_id) {
          projectAPI.getProject(taskData.project_id).then((project) => {
            if (project?.key) {
              setProjectKey(project.key);
            }
          }).catch((err) => {
            console.error('Failed to fetch project:', err);
          });
        }

        // Load related data
        const [members, statuses, prios, commentsData] = await Promise.all([
          taskData.project_id ? projectMemberAPI.getProjectMembers(taskData.project_id).catch(() => []) : Promise.resolve([]),
          mastersAPI.getWorkflowStatuses().catch(() => []),
          mastersAPI.getPriorities().catch(() => []),
          commentAPI.getComments(taskId).catch(() => []),
        ]);

        setProjectMembers(members || []);
        setWorkflowStatuses((statuses || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
        setPriorities(prios || []);
        setComments(commentsData || []);
        setActivity(commentsData || []);

        // Open modal once data is loaded
        setIsModalOpen(true);
      } catch (err: any) {
        console.error('Failed to load task:', err);
        setError(err?.response?.data?.detail || 'Failed to load task');
        hasLoadedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    loadTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]); // Only depend on taskId to prevent infinite loops

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Don't navigate if we're opening a subtask (navigation will happen when subtask modal closes)
    if (isOpeningSubtaskRef.current) {
      isOpeningSubtaskRef.current = false;
      return;
    }
    // Navigate to project page if task has project_id, otherwise go to dashboard
    if (task?.project_id) {
      navigate(`/projects/${task.project_id}/board`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleStatusUpdate = async (taskId: string, newStatus: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: newStatus || undefined }
      })).unwrap();
      
      // Refresh task data
      const fresh = await taskAPI.getTask(taskId);
      setTask(fresh);
    } catch (e) {
      console.error('Status update failed', e);
    }
  };

  const handleAddComment = async (taskId: string, comment: string, parentId?: string): Promise<Comment> => {
    const created = await commentAPI.createComment({
      task_id: taskId,
      content: comment,
      parent_comment_id: parentId,
    });
    const data = await commentAPI.getComments(taskId);
    setComments(data || []);
    setActivity(data || []);
    return created;
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    if (taskId) {
      const data = await commentAPI.getComments(taskId);
      setComments(data || []);
      setActivity(data || []);
    }
  };

  const handleUpdateComment = async (commentId: string, content: string) => {
    await updateComment(commentId, content);
    if (taskId) {
      const data = await commentAPI.getComments(taskId);
      setComments(data || []);
      setActivity(data || []);
    }
  };

  const handleUpdateDescription = async (taskId: string, description: string) => {
    await dispatch(updateTask({ id: taskId, data: { description } })).unwrap();
    if (task) {
      const fresh = await taskAPI.getTask(taskId);
      setTask(fresh);
    }
  };

  const handleUpdateAssignee = async (taskId: string, assigneeId: string | null) => {
    await dispatch(updateTask({ id: taskId, data: { assigned_to: assigneeId || undefined } })).unwrap();
    if (task) {
      const fresh = await taskAPI.getTask(taskId);
      setTask(fresh);
    }
  };

  const handleUpdateReporter = async (taskId: string, reporterId: string | null) => {
    await dispatch(updateTask({ id: taskId, data: { reporter_id: reporterId || undefined } })).unwrap();
    if (task) {
      const fresh = await taskAPI.getTask(taskId);
      setTask(fresh);
    }
  };

  const handleUpdateTitle = async (taskId: string, content: string) => {
    await dispatch(updateTask({ id: taskId, data: { title: content } })).unwrap();
    if (task) {
      const fresh = await taskAPI.getTask(taskId);
      setTask(fresh);
    }
  };

  const handleUpdateFields = async (taskId: string) => {
    // Refresh task data after field updates to reflect changes in UI immediately
    try {
      const fresh = await taskAPI.getTask(taskId);
      // Force update by creating a new object reference
      setTask({ ...fresh });
      // The DetailsSidebar will sync with the updated task prop via its useEffects
      // which watch for changes in task?.sprint_id, task?.due_date, task?.labels, etc.
    } catch (err) {
      console.error('Failed to refresh task after field update:', err);
    }
  };

  const handleOpenSubtask = (subtask: Subtask, parentTask: Task) => {
    // Don't close task modal - just open subtask modal on top
    // The task modal will stay open in the background
    setParentTaskForSubtask(parentTask);
    setActiveSubtask(subtask);
    setIsSubtaskModalOpen(true);
  };

  const handleCloseSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
    // Task modal should already be open, no need to reopen
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader variant="overlay" size="lg" text="Loading task..." />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Task not found</h2>
          <p className="text-gray-600 mb-4">{error || 'The task you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to For You
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <TaskModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        task={task}
        onStatusUpdate={(taskId, newStatus) => handleStatusUpdate(taskId, newStatus)}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        onUpdateDescription={handleUpdateDescription}
        onUpdateAssignee={handleUpdateAssignee}
        onUpdateReporter={handleUpdateReporter}
        onUpdateComment={handleUpdateComment}
        onUpdateTaskTitle={handleUpdateTitle}
        onUpdateFields={handleUpdateFields}
        onOpenSubtask={handleOpenSubtask}
        activity={activity}
        taskDetails={{}}
        projectMembers={projectMembers}
        comments={comments}
        projectKey={projectKey}
        workflowStatuses={workflowStatuses}
        priorities={priorities}
        project={currentProject}
      />

      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={handleCloseSubtaskModal}
          parentKey={`${projectKey}-${(parentTaskForSubtask || task)?.display_number ?? ''}`.replace(/-$/, '')}
          parentTitle={(parentTaskForSubtask || task)?.title}
          parentTask={(parentTaskForSubtask || task) as any}
          onOpenParent={(_parent) => {
            // Close subtask modal and open parent task modal
            handleCloseSubtaskModal();
            if (_parent) {
              setTask(_parent);
              setIsModalOpen(true);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={projectKey}
          project={currentProject}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          onSave={async (updates) => {
            await subtaskAPI.update(activeSubtask.id, updates);
            const fresh = await subtaskAPI.get(activeSubtask.id);
            setActiveSubtask(fresh);
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
        />
      )}
    </>
  );
};

